import type { FofFundRow, SignalHealthFund, SignalHealthStatus } from '../api/client'

// Pure helpers for the signal-health tiles / panel / FOF column (kept out of
// <script setup> so they can be unit-tested — same convention as pricingSourceHelpers.ts).

export function statusColour(s: SignalHealthStatus | null): string {
  switch (s) {
    case 'green': return '#4caf50'
    case 'amber': return '#fbc02d'
    case 'red': return '#f44336'
    default: return '#888'
  }
}

// Red reads WATCH (the name of the agreed trigger), not "Red".
export function statusWord(s: SignalHealthStatus | null): string {
  return s === 'green' ? 'OK' : s === 'amber' ? 'AMBER' : s === 'red' ? 'WATCH' : '—'
}

function num(v: number | null | undefined): v is number {
  return v !== null && v !== undefined && !Number.isNaN(v)
}

export function fmtBeta(v: number | null | undefined): string {
  return num(v) ? v.toFixed(2) : '—'
}

export function fmtBps(v: number | null | undefined): string {
  return num(v) ? `${Math.round(v)} bp` : '—'
}

export function fmtHit(v: number | null | undefined): string {
  return num(v) ? `${(v * 100).toFixed(1)}%` : '—'
}

export function fmtP(v: number | null | undefined): string {
  return num(v) ? v.toFixed(2) : '—'
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Fixed month names: toLocaleDateString('en-GB') prints "Sept" on newer ICU builds.
export function fmtAckDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return `${d} ${MONTHS[m - 1]} ${y}`
}

export function isAcknowledged(f: Pick<SignalHealthFund, 'acknowledgedUntil'>, todayIso: string): boolean {
  return !!f.acknowledgedUntil && todayIso <= f.acknowledgedUntil
}

export function triggerLevel(f: SignalHealthFund): number | null {
  return f.trigger.betaFloor
}

// SVG polyline over the non-null values, spaced evenly across `width`; the y-scale
// spans the values and the optional level line together (5% padding; flat range → ±0.1).
export function sparkline(
  values: (number | null)[], level: number | null, width: number, height: number,
): { points: string; levelY: number | null } {
  const vs = values.filter((v): v is number => num(v))
  if (vs.length === 0) return { points: '', levelY: null }
  const all = num(level) ? [...vs, level] : vs
  const lo0 = Math.min(...all)
  const hi0 = Math.max(...all)
  const range = hi0 - lo0
  const pad = range === 0 ? 0.1 : range * 0.05
  const lo = lo0 - pad
  const span = hi0 + pad - lo
  const y = (v: number) => height - ((v - lo) / span) * height
  const r = (n: number) => Math.round(n * 100) / 100
  const step = vs.length > 1 ? width / (vs.length - 1) : 0
  const points = vs.map((v, i) => `${r(i * step)},${r(y(v))}`).join(' ')
  return { points, levelY: num(level) ? r(y(level)) : null }
}

export function fofHealthInline(row: Pick<FofFundRow, 'health' | 'healthSummary'>): string {
  const word = statusWord(row.health)
  return row.health === 'red' && row.healthSummary ? `${word} · ${row.healthSummary}` : word
}
